/**
 * Domain types and the derived views the UI is built from.
 *
 * Everything here is a pure function over assignment lists, which makes the
 * behaviour that matters most — who owns a day, how time splits across people,
 * what happens next — unit-testable without a browser or a database.
 *
 * "Nicht zugewiesen" (unassigned) is never stored: it is the absence of an
 * assignment and is represented as `personId: null` in derived structures.
 */

import {
  MINUTES_PER_SLOT,
  SLOTS_PER_DAY,
  clipInterval,
  dayEndSlot,
  dayOfSlot,
  dayStartSlot,
  localDateFromDay,
} from './slots.js';

// ---------------------------------------------------------------------------
// Transport types
// ---------------------------------------------------------------------------

export interface PersonDto {
  id: number;
  name: string;
  colorIndex: number;
  hasAccount: boolean;
  isAdmin: boolean;
  /**
   * A Betreuungsperson: care time may be assigned to them and they may change
   * the calendar. An account with this cleared is view-only. Cleared without an
   * account is not a state the API allows.
   */
  isCaregiver: boolean;
  createdAt: number;
  /** Number of assignments referencing this person; blocks deletion when > 0. */
  assignmentCount: number;
}

export interface AssignmentDto {
  id: number;
  personId: number;
  startSlot: number;
  /** Exclusive end — an assignment covers `[startSlot, endSlot)`. */
  endSlot: number;
  /** Optimistic concurrency token; must be echoed back when updating. */
  updatedAt: number;
}

export type ChangelogEntity = 'assignment' | 'person';
export type ChangelogAction = 'created' | 'updated' | 'deleted';

/**
 * Assignment state as recorded in the log. The person name is denormalised so
 * old entries stay readable after a rename or deletion.
 */
export interface AssignmentSnapshot {
  personId: number;
  personName: string;
  colorIndex: number;
  startSlot: number;
  endSlot: number;
}

/** Person state as recorded in the log. Never contains the password hash. */
export interface PersonSnapshot {
  id: number;
  name: string;
  colorIndex: number;
  hasAccount: boolean;
  isAdmin: boolean;
  /**
   * Optional because snapshots are stored as JSON and read back with a cast:
   * entries written before the role existed simply do not carry it. Diffs must
   * skip the field when either side is `undefined` rather than read it as
   * `false`, which would report a change that never happened.
   */
  isCaregiver?: boolean;
  /** Whether a password was set — lets the log show resets without leaking it. */
  hasPassword: boolean;
}

export type ChangelogSnapshot = AssignmentSnapshot | PersonSnapshot;

export interface ChangelogEntryDto {
  id: number;
  /** `null` for entries written by the system (bootstrap, seed). */
  actorPersonId: number | null;
  actorName: string | null;
  ts: number;
  entity: ChangelogEntity;
  action: ChangelogAction;
  entityId: number;
  before: ChangelogSnapshot | null;
  after: ChangelogSnapshot | null;
}

export interface SessionUserDto {
  personId: number;
  name: string;
  colorIndex: number;
  isAdmin: boolean;
  /** Cleared for a view-only account: the client renders no write affordances. */
  isCaregiver: boolean;
}

export function isAssignmentSnapshot(
  snapshot: ChangelogSnapshot | null,
): snapshot is AssignmentSnapshot {
  return snapshot !== null && 'startSlot' in snapshot;
}

export function isPersonSnapshot(snapshot: ChangelogSnapshot | null): snapshot is PersonSnapshot {
  return snapshot !== null && 'hasAccount' in snapshot;
}

// ---------------------------------------------------------------------------
// Day segments
// ---------------------------------------------------------------------------

/** A run of consecutive slots inside one day belonging to the same owner. */
export interface DaySegment {
  /** Inclusive start, `0 … 47`. */
  fromSlotInDay: number;
  /** Exclusive end, `1 … 48`. */
  toSlotInDay: number;
  /** `null` for unassigned time. */
  personId: number | null;
}

/**
 * Chronological runs across the 48 slots of a day. Unassigned stretches are
 * returned as segments of their own (never omitted) so the time band can render
 * them as grey.
 *
 * Assignments are guaranteed not to overlap by the server; should they ever,
 * the later one in the input wins for the overlapping slots.
 */
export function daySegments(
  assignments: readonly AssignmentDto[],
  dayIndex: number,
): DaySegment[] {
  const dayStart = dayStartSlot(dayIndex);
  const dayEnd = dayEndSlot(dayIndex);
  const owners: (number | null)[] = new Array(SLOTS_PER_DAY).fill(null);

  for (const assignment of assignments) {
    const clipped = clipInterval(assignment.startSlot, assignment.endSlot, dayStart, dayEnd);
    if (!clipped) continue;
    for (let slot = clipped.fromSlot; slot < clipped.toSlot; slot += 1) {
      owners[slot - dayStart] = assignment.personId;
    }
  }

  const segments: DaySegment[] = [];
  let current: DaySegment | null = null;
  for (let index = 0; index < SLOTS_PER_DAY; index += 1) {
    const owner = owners[index] ?? null;
    if (current && current.personId === owner) {
      current.toSlotInDay = index + 1;
    } else {
      current = { fromSlotInDay: index, toSlotInDay: index + 1, personId: owner };
      segments.push(current);
    }
  }
  return segments;
}

/**
 * Person covering most of the day. Ties are broken in favour of whoever appears
 * earlier in the day. Unassigned time can win — such a day renders as an empty
 * (dashed) cell.
 */
export function dominantPersonOfDay(segments: readonly DaySegment[]): number | null {
  const totals = new Map<number | null, number>();
  const firstSeen = new Map<number | null, number>();

  for (const segment of segments) {
    const length = segment.toSlotInDay - segment.fromSlotInDay;
    totals.set(segment.personId, (totals.get(segment.personId) ?? 0) + length);
    if (!firstSeen.has(segment.personId)) firstSeen.set(segment.personId, segment.fromSlotInDay);
  }

  let winner: number | null = null;
  let winnerSlots = -1;
  let winnerFirstSeen = Number.POSITIVE_INFINITY;
  for (const [personId, slots] of totals) {
    const seen = firstSeen.get(personId) ?? Number.POSITIVE_INFINITY;
    if (slots > winnerSlots || (slots === winnerSlots && seen < winnerFirstSeen)) {
      winner = personId;
      winnerSlots = slots;
      winnerFirstSeen = seen;
    }
  }
  return winner;
}

/** Convenience: dominant person of a day straight from an assignment list. */
export function dominantPersonOfDayFromAssignments(
  assignments: readonly AssignmentDto[],
  dayIndex: number,
): number | null {
  return dominantPersonOfDay(daySegments(assignments, dayIndex));
}

// ---------------------------------------------------------------------------
// Statistics
// ---------------------------------------------------------------------------

export interface StatRow {
  /** `null` is the mandatory "Nicht zugewiesen" row. */
  personId: number | null;
  slots: number;
  minutes: number;
  /** Share of the period, `0 … 1`. */
  fraction: number;
}

export interface StatSummary {
  rows: StatRow[];
  totalSlots: number;
  totalMinutes: number;
  /** Whole days in the period — the period always covers full days. */
  days: number;
}

/**
 * Minutes per person inside `[fromSlot, toSlot)`, clipped to the window and
 * sorted by share descending. Unassigned time is always present as its own row,
 * even when it is zero, because the specification requires the line to show.
 */
export function aggregateRange(
  assignments: readonly AssignmentDto[],
  fromSlot: number,
  toSlot: number,
): StatSummary {
  const totalSlots = Math.max(0, toSlot - fromSlot);
  const slotsByPerson = new Map<number, number>();
  const firstSlotByPerson = new Map<number, number>();
  let assignedSlots = 0;

  for (const assignment of assignments) {
    const clipped = clipInterval(assignment.startSlot, assignment.endSlot, fromSlot, toSlot);
    if (!clipped) continue;
    const length = clipped.toSlot - clipped.fromSlot;
    slotsByPerson.set(assignment.personId, (slotsByPerson.get(assignment.personId) ?? 0) + length);
    const previous = firstSlotByPerson.get(assignment.personId);
    if (previous === undefined || clipped.fromSlot < previous) {
      firstSlotByPerson.set(assignment.personId, clipped.fromSlot);
    }
    assignedSlots += length;
  }

  const unassignedSlots = Math.max(0, totalSlots - assignedSlots);
  const entries: { personId: number | null; slots: number; firstSlot: number }[] = [];
  for (const [personId, slots] of slotsByPerson) {
    entries.push({ personId, slots, firstSlot: firstSlotByPerson.get(personId) ?? fromSlot });
  }
  entries.push({
    personId: null,
    slots: unassignedSlots,
    firstSlot: firstUnassignedSlot(assignments, fromSlot, toSlot),
  });

  entries.sort((a, b) => b.slots - a.slots || a.firstSlot - b.firstSlot);

  return {
    rows: entries.map((entry) => ({
      personId: entry.personId,
      slots: entry.slots,
      minutes: entry.slots * MINUTES_PER_SLOT,
      fraction: totalSlots > 0 ? entry.slots / totalSlots : 0,
    })),
    totalSlots,
    totalMinutes: totalSlots * MINUTES_PER_SLOT,
    days: Math.round(totalSlots / SLOTS_PER_DAY),
  };
}

/**
 * First slot in the window that no assignment covers. Only used to give the
 * unassigned row a deterministic position when shares are tied.
 */
function firstUnassignedSlot(
  assignments: readonly AssignmentDto[],
  fromSlot: number,
  toSlot: number,
): number {
  const covering = assignments
    .filter((assignment) => assignment.startSlot < toSlot && assignment.endSlot > fromSlot)
    .sort((a, b) => a.startSlot - b.startSlot);
  let cursor = fromSlot;
  for (const assignment of covering) {
    if (assignment.startSlot > cursor) return cursor;
    cursor = Math.max(cursor, assignment.endSlot);
    if (cursor >= toSlot) return toSlot;
  }
  return cursor;
}

// ---------------------------------------------------------------------------
// "Aktuell" schedule list
// ---------------------------------------------------------------------------

export type ScheduleRow =
  | { kind: 'running'; assignment: AssignmentDto; nextPersonId: number | null }
  | { kind: 'unassignedNow'; untilSlot: number | null }
  | { kind: 'monthDivider'; year: number; month: number }
  | { kind: 'assignment'; assignment: AssignmentDto }
  | { kind: 'gap'; startSlot: number; endSlot: number };

/**
 * Turns the upcoming assignments into the rows of the "Aktuell" tab: the
 * running card first, then every future assignment in order with unassigned
 * gaps inserted as their own rows and a divider whenever the month changes.
 *
 * `assignments` must be ordered by `startSlot` and may only contain entries
 * that end after `nowSlot` — that is exactly what the upcoming endpoint returns.
 */
export function buildScheduleRows(
  assignments: readonly AssignmentDto[],
  nowSlot: number,
): ScheduleRow[] {
  const rows: ScheduleRow[] = [];
  const upcoming = [...assignments].sort((a, b) => a.startSlot - b.startSlot);

  const running = upcoming.find(
    (assignment) => assignment.startSlot <= nowSlot && assignment.endSlot > nowSlot,
  );
  const future = upcoming.filter((assignment) => assignment.startSlot > nowSlot);

  if (running) {
    const next = future[0];
    const nextPersonId = next && next.startSlot === running.endSlot ? next.personId : null;
    rows.push({ kind: 'running', assignment: running, nextPersonId });
  } else {
    rows.push({ kind: 'unassignedNow', untilSlot: future[0]?.startSlot ?? null });
  }

  // When nothing is running, the "unassignedNow" row already represents the gap
  // between now and the next assignment, so the loop must not emit it twice.
  let cursor = running ? running.endSlot : (future[0]?.startSlot ?? nowSlot);
  // Month of the previously emitted list row; the very first row never gets a
  // divider because the running card already carries the current date.
  let previousMonthKey: string | null = null;

  const pushRow = (row: ScheduleRow, anchorSlot: number) => {
    const { year, month } = localDateFromDay(dayOfSlot(anchorSlot));
    const monthKey = `${year}-${month}`;
    if (previousMonthKey !== null && previousMonthKey !== monthKey) {
      rows.push({ kind: 'monthDivider', year, month });
    }
    previousMonthKey = monthKey;
    rows.push(row);
  };

  for (const assignment of future) {
    if (assignment.startSlot > cursor) {
      pushRow({ kind: 'gap', startSlot: cursor, endSlot: assignment.startSlot }, cursor);
    }
    pushRow({ kind: 'assignment', assignment }, assignment.startSlot);
    cursor = Math.max(cursor, assignment.endSlot);
  }

  return rows;
}

/** Assignment covering a given slot, if any. */
export function assignmentAtSlot(
  assignments: readonly AssignmentDto[],
  slot: number,
): AssignmentDto | undefined {
  return assignments.find((assignment) => assignment.startSlot <= slot && assignment.endSlot > slot);
}

/** Does the range touch the past? Used to gate the confirmation dialog. */
export function isInPast(startSlot: number, nowSlot: number): boolean {
  return startSlot < nowSlot;
}
